import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { Prohibit, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Nav } from "../components/Nav";
import api, { formatApiErrorDetail } from "../api";

export default function AdminPage() {
  const { user } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || user.role !== "admin") {
      setLoading(false);
      return;
    }
    Promise.all([api.get("/appointments"), api.get("/messages"), api.get("/users")])
      .then(([a, m, u]) => {
        setAppointments(a.data);
        setMessages(m.data);
        setUsers(u.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  const toggleBan = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.id}/ban`);
      setUsers((list) => list.map((x) => (x.id === u.id ? { ...x, banned: data.banned } : x)));
      toast.success(data.banned ? `${u.name} banned` : `${u.name} unbanned`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const removeUser = async (u) => {
    try {
      await api.delete(`/users/${u.id}`);
      setUsers((list) => list.filter((x) => x.id !== u.id));
      toast.success(`${u.name} removed`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  let body;
  if (user === null || loading) {
    body = <p data-testid="admin-loading" className="text-white/40 uppercase tracking-widest text-sm">Loading...</p>;
  } else if (!user || user.role !== "admin") {
    body = (
      <div data-testid="admin-unauthorized">
        <p className="font-display uppercase text-3xl text-white/40">Owner access only</p>
        <p className="text-white/50 mt-4 text-sm">
          Sign in with the owner account on the{" "}
          <Link to="/forum" className="text-primary hover:text-[#FF7733] transition-colors">forum page</Link> to view requests.
        </p>
      </div>
    );
  } else {
    body = (
      <>
        <h2 className="font-display uppercase text-3xl tracking-tight mb-6 text-violet-400">Registered Users</h2>
        {users.length === 0 ? (
          <p data-testid="admin-users-empty" className="text-white/40 text-sm mb-16">No registered users yet.</p>
        ) : (
          <div className="mb-16" data-testid="admin-users-list">
            {users.map((u) => (
              <div key={u.id} data-testid={`admin-user-${u.id}`} className="grid md:grid-cols-12 gap-3 border-b border-white/10 py-5 text-sm items-center">
                <p className="md:col-span-4 font-semibold flex items-center gap-3 flex-wrap">
                  {u.name}
                  {u.role === "admin" && (
                    <span className="px-2 py-0.5 text-[10px] uppercase tracking-widest bg-primary text-white">Owner</span>
                  )}
                  {u.banned && (
                    <span className="px-2 py-0.5 text-[10px] uppercase tracking-widest bg-red-500/20 text-red-400 border border-red-400/40">Banned</span>
                  )}
                </p>
                <p className="md:col-span-4 text-white/60">{u.email}</p>
                <p className="md:col-span-2 text-white/40">{format(new Date(u.created_at), "MMM d, yyyy")}</p>
                <div className="md:col-span-2 flex gap-4 md:justify-end">
                  {u.role !== "admin" && (
                    <>
                      <button
                        data-testid={`admin-user-ban-${u.id}`}
                        onClick={() => toggleBan(u)}
                        className={`flex items-center gap-2 text-xs uppercase tracking-widest transition-colors ${u.banned ? "text-violet-400 hover:text-violet-300" : "text-white/40 hover:text-primary"}`}
                      >
                        <Prohibit size={15} /> {u.banned ? "Unban" : "Ban"}
                      </button>
                      <button
                        data-testid={`admin-user-remove-${u.id}`}
                        onClick={() => removeUser(u)}
                        className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/40 hover:text-red-400 transition-colors"
                      >
                        <Trash size={15} /> Remove
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <h2 className="font-display uppercase text-3xl tracking-tight mb-6 text-primary">Appointment Requests</h2>
        {appointments.length === 0 ? (
          <p data-testid="admin-appointments-empty" className="text-white/40 text-sm mb-16">No appointment requests yet.</p>
        ) : (
          <div className="mb-16">
            {appointments.map((a) => (
              <div key={a.id} data-testid={`admin-appointment-${a.id}`} className="grid md:grid-cols-12 gap-3 border-b border-white/10 py-5 text-sm">
                <p className="md:col-span-3 font-semibold">{a.name}</p>
                <p className="md:col-span-3 text-white/60">{a.contact}</p>
                <p className="md:col-span-2 text-primary">{a.when.replace("T", " · ")}</p>
                <p className="md:col-span-4 text-white/60">{a.reason}</p>
              </div>
            ))}
          </div>
        )}

        <h2 className="font-display uppercase text-3xl tracking-tight mb-6 text-primary">Messages</h2>
        {messages.length === 0 ? (
          <p data-testid="admin-messages-empty" className="text-white/40 text-sm">No messages yet.</p>
        ) : (
          messages.map((m) => (
            <div key={m.id} data-testid={`admin-message-${m.id}`} className="border-b border-white/10 py-5 text-sm">
              <p className="font-semibold">
                {m.name} <span className="text-white/40 font-normal">· {m.contact} · {format(new Date(m.created_at), "MMM d, yyyy")}</span>
              </p>
              <p className="text-white/60 mt-2 max-w-2xl whitespace-pre-line">{m.message}</p>
            </div>
          ))
        )}
      </>
    );
  }

  return (
    <div data-testid="admin-page" className="bg-[#050505] text-white min-h-screen">
      <Nav />
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="px-6 md:px-12 pt-44 md:pt-52 pb-24"
      >
        <p className="text-primary text-xs tracking-[0.35em] uppercase mb-4">Owner Dashboard</p>
        <h1 className="font-display uppercase tracking-tighter text-5xl md:text-6xl leading-[0.95] mb-12">Requests</h1>
        {body}
      </motion.div>
    </div>
  );
}
